import React, { ForwardedRef, forwardRef, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import IconButton from "./IconButton";
import getclassNames from "../../utils/getClassNames";
import './DropDownWithPortal.css';

interface DropdownProps {
    id: string;
    options: { label: string, href?: string, onClick?: () => void, newWindow?: boolean; disabled?: boolean; }[];
    openTop?: boolean;
    disabled?: boolean;
    optionSize?: 'small' | 'medium' | 'large';
    buttonLabel?: string;
    buttonClassName?: string;
}

const DropDownWithPortal = forwardRef((props: DropdownProps, ref?: ForwardedRef<any>) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleCloseOthers = (e: CustomEvent) => {
            if (e.detail.dropdownId !== props.id) {
                setVisible(false);
            }
        };

        document.addEventListener('closeOtherDropdowns', handleCloseOthers as EventListener);

        return () => {
            document.removeEventListener('closeOtherDropdowns', handleCloseOthers as EventListener);
        };
    }, [props.id]);

    useEffect(() => {
        if (visible) {
            const close = (e: MouseEvent) => {
                const target = e.target as Node;
                if (
                    triggerRef.current && 
                    !triggerRef.current.contains(target) &&
                    dropdownRef.current &&
                    !dropdownRef.current.contains(target)
                ) {
                    setVisible(false);
                }
            };

            document.addEventListener('click', close);
            window.addEventListener('resize', () => setVisible(false));
            window.addEventListener('scroll', () => setVisible(false), true);

            return () => {
                document.removeEventListener('click', close);
                window.removeEventListener('resize', () => setVisible(false));
                window.removeEventListener('scroll', () => setVisible(false), true);
            };
        }
    }, [visible]);

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        const closeEvent = new CustomEvent('closeOtherDropdowns', { 
            detail: { dropdownId: props.id } 
        });
        document.dispatchEvent(closeEvent);

        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({
                top: props.openTop ? rect.top : rect.bottom,
                left: rect.left
            });
        }
        setVisible(!visible);
    };

    const dropdownContent = (
        <div 
            ref={dropdownRef}
            className={getclassNames(
                'portal-dropdown-contents', 
                props.openTop ? `top-${props.options.length}` : '',
                props.buttonLabel ? 'has-button' : ''
            )}
            style={{ top: coords.top, left: coords.left }}
        >
            {props.options.map((o, i) => {
                const className = getclassNames('dropdown-option', props.optionSize ?? 'medium', (props.disabled || o.disabled) ? 'disabled' : '');
                
                if (props.disabled || o.disabled) {
                    return <div key={i} className={className}>{o.label}</div>;
                }
                if (o.href) {
                    return <Link key={i} to={o.href} className={className} target={o.newWindow ? '_blank' : '_self'}>{o.label}</Link>;
                }
                return <div key={i} onClick={o.onClick} className={className}>{o.label}</div>;
            })}
        </div>
    );

    return (
        <div className={getclassNames('dropdown-container', props.buttonLabel ? 'has-button' : '')} 
             ref={(node) => {
                 (triggerRef as any).current = node;
                 if (typeof ref === 'function') ref(node);
                 else if (ref) (ref as any).current = node;
             }}>
            {props.buttonLabel ? (
                <button type="button" id={props.id} onClick={handleOpen} disabled={props.disabled}
                    className={getclassNames('button dropdown-button', props.buttonClassName || '')}>
                    <span className="button-text">{props.buttonLabel}</span>
                    <i className={getclassNames('ri-arrow-down-s-line', visible ? 'dropdown-arrow-up' : '')}></i>
                </button>
            ) : (
                <IconButton className="flat" id={props.id} onClick={handleOpen} 
                    icon={<i className="ri-more-2-line"></i>} />
            )}
            
            {visible && createPortal(dropdownContent, document.body)}
        </div>
    );
});

export default DropDownWithPortal;